import axios from "axios";
import { fetchSessionToken } from "./shopifyAppBridge";
import { logger } from "./logger";

// Configure Axios defaults
// Point to backend API prefix so Vite proxy (/api -> backend) works in dev
axios.defaults.baseURL = "/api";
axios.defaults.withCredentials = true;
axios.defaults.headers.common["Content-Type"] = "application/json";
axios.defaults.headers.common["Accept"] = "application/json";

// Track pending session token fetches to avoid multiple concurrent calls
let sessionTokenPromise: Promise<string | null> | null = null;
let sessionTokenCache: string | null = null;
let sessionTokenCacheTime: number = 0;
const SESSION_TOKEN_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Invalidate the cached Shopify session token. MUST be called on every
// login/logout so a token minted for account A can never be attached to
// requests made after account B signs in (cross-account token leakage).
export function clearSessionTokenCache(): void {
    sessionTokenCache = null;
    sessionTokenCacheTime = 0;
    sessionTokenPromise = null;
}

// Request timeout to prevent stuck requests
const REQUEST_TIMEOUT = 30000; // 30 seconds

// Add request interceptor for debugging and JWT token
axios.interceptors.request.use(
    async (config) => {
        const startTime = performance.now();
        (config as any).__startTime = startTime;
        
        // Add timeout to prevent stuck requests
        config.timeout = config.timeout || REQUEST_TIMEOUT;
        
        logger.api(
            config.method?.toUpperCase() || 'UNKNOWN',
            config.url || 'unknown',
            config.data,
        );

        if (typeof window !== 'undefined') {
            // Fetch session token with caching and timeout protection
            try {
                const now = Date.now();
                // Use cached token if still valid
                if (sessionTokenCache && (now - sessionTokenCacheTime) < SESSION_TOKEN_CACHE_DURATION) {
                    config.headers = config.headers || {};
                    config.headers['X-Shopify-Session-Token'] = sessionTokenCache;
                } else {
                    // Only fetch if not already fetching
                    if (!sessionTokenPromise) {
                        sessionTokenPromise = Promise.race([
                            fetchSessionToken(),
                            new Promise<string | null>((resolve) => 
                                setTimeout(() => resolve(null), 5000) // 5 second timeout
                            )
                        ]).finally(() => {
                            sessionTokenPromise = null;
                        });
                    }
                    
                    const sessionToken = await sessionTokenPromise;
                    if (sessionToken) {
                        sessionTokenCache = sessionToken;
                        sessionTokenCacheTime = now;
                        config.headers = config.headers || {};
                        config.headers['X-Shopify-Session-Token'] = sessionToken;
                    }
                }
            } catch (error) {
                logger.warn('AXIOS', 'Failed to fetch session token, continuing without it', error);
                // Continue without session token - not critical for all requests
            }

            const token = localStorage.getItem('token');
            const adminToken = localStorage.getItem('admin_token');
            
            // Use admin token for admin routes, regular token for others
            const authToken = config.url?.startsWith('/admin') ? adminToken : token;
            
            if (authToken) {
                config.headers = config.headers || {};
                config.headers.Authorization = `Bearer ${authToken}`;
            }
        }

        return config;
    },
    (error) => {
        logger.error('AXIOS', 'Request interceptor error', error);
        return Promise.reject(error);
    }
);

// Track redirect to prevent multiple redirects
let isRedirecting = false;

// Add response interceptor for debugging and auth error handling
axios.interceptors.response.use(
    (response) => {
        const startTime = (response.config as any).__startTime;
        const responseTime = startTime ? Math.round(performance.now() - startTime) : null;
        
        logger.api(
            response.config.method?.toUpperCase() || 'UNKNOWN',
            response.config.url || 'unknown',
            response.config.data,
            {
                status: response.status,
                statusText: response.statusText,
                data: response.data,
                responseTime,
            }
        );
        
        return response;
    },
    (error) => {
        const url = error.config?.url || 'unknown';
        const status = error.response?.status;
        const startTime = (error.config as any)?.__startTime;
        const responseTime = startTime ? Math.round(performance.now() - startTime) : null;
        
        logger.api(
            error.config?.method?.toUpperCase() || 'UNKNOWN',
            url,
            error.config?.data,
            null,
            {
                status,
                statusText: error.response?.statusText,
                data: error.response?.data,
                message: error.message,
                responseTime,
            }
        );

        // Handle timeout errors
        if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
            logger.error('AXIOS', `Request timeout - request took too long: ${url}`, error);
            return Promise.reject(new Error('Request timeout. Please try again.'));
        }

        // Handle network errors
        if (!error.response) {
            logger.error('AXIOS', `Network error - no response received: ${url}`, error);
            return Promise.reject(new Error('Network error. Please check your connection.'));
        }

        // Handle 401/403 errors.
        // Important: don't clear stored token for "Token not provided" style responses
        // when this specific request did not actually send Authorization header.
        if (status === 401 || status === 403) {
            const token = localStorage.getItem('token');
            if (token) {
                const sentAuthHeader =
                    Boolean(error.config?.headers?.Authorization) ||
                    Boolean(error.config?.headers?.authorization);
                const responseMessage = String(error.response?.data?.message || '').toLowerCase();
                const looksLikeInvalidToken =
                    responseMessage.includes('token expired') ||
                    responseMessage.includes('token invalid') ||
                    responseMessage.includes('invalid token') ||
                    responseMessage.includes('signature') ||
                    responseMessage.includes('unauthenticated');

                // Do not treat every authenticated 401 as "session expired".
                // Only clear token for explicit auth/session checks or clear token-invalid messages.
                const isAuthCheckRequest =
                    url.includes('/me') || url.includes('/login') || url.includes('/register') || url.includes('/logout');
                const shouldClearToken = isAuthCheckRequest || looksLikeInvalidToken;

                if (shouldClearToken) {
                    logger.warn('AXIOS', 'Token expired/invalid, clearing stored auth...', {
                        url,
                        status,
                        sentAuthHeader,
                        responseMessage,
                    });
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                } else {
                    logger.warn('AXIOS', '401/403 received without auth header on request; keeping stored token', {
                        url,
                        status,
                        responseMessage,
                    });
                }
                
                // Clear session token cache on auth failure
                sessionTokenCache = null;
                sessionTokenCacheTime = 0;

                // Only redirect if we're not already on the login/signup page
                // and the request wasn't the /me endpoint (let AuthContext handle that)
                const isAuthCheck = isAuthCheckRequest;
                const isAuthPage = window.location.pathname === '/signin' || 
                                  window.location.pathname === '/signup' ||
                                  window.location.pathname === '/billing/choose-plan';
                const isShopifyApp = window.location.pathname === '/shopify-app';

                // Don't redirect if:
                // 1. Already on auth page
                // 2. It's an auth check request (let AuthContext handle it)
                // 3. Already redirecting
                // 4. On shopify-app page (let it handle its own auth)
                if (shouldClearToken && !isAuthPage && !isAuthCheck && !isRedirecting && !isShopifyApp) {
                    isRedirecting = true;
                    // Delay redirect to allow current request to complete
                    setTimeout(() => {
                        window.location.href = '/signin';
                        isRedirecting = false;
                    }, 100);
                }
            }
        }

        // Handle 429 (Too Many Requests) - rate limiting
        if (status === 429) {
            logger.warn('AXIOS', 'Rate limit exceeded, please slow down requests', { url, status });
            return Promise.reject(new Error('Too many requests. Please wait a moment and try again.'));
        }

        // Handle 500+ server errors
        if (status >= 500) {
            logger.error('AXIOS', `Server error: ${status}`, { url, status, data: error.response?.data });
            return Promise.reject(new Error('Server error. Please try again later.'));
        }

        return Promise.reject(error);
    }
);

export default axios;
